import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { canAccess } from 'app/access';
import { useAuth } from 'hooks/context/useAuthContext';
import { useOptimisticSearchParams } from 'nuqs/adapters/react-router/v7';

interface RequireAuthProps {
  children: React.ReactNode;
  permissions?: string | string[];
  internalRequired?: boolean;
}

const RequireAuth = ({ permissions, internalRequired = false, children }: RequireAuthProps) => {
  const { internal, getToken, hasTokenExpired, hasPrivilege } = useAuth();
  const location = useLocation();
  const searchParams = useOptimisticSearchParams();
  const navigate = useNavigate();
  const internalVerification = !internalRequired || internal;
  const missingPermissions = [permissions ?? []].flat().filter((permission) => !hasPrivilege(permission));
  const urlPath = permissions
    ? `/unauthorized?privileges=${!internalVerification ? 'ENTUR intern bruker' : missingPermissions}`
    : '/unauthorized';

  const token = getToken();
  const tokenExpired = hasTokenExpired();
  const hasAccess = canAccess({ permissions, internalRequired }, { hasPrivilege, internal });

  const redirectTarget = useMemo(() => {
    if (!token) {
      return {
        path: '/login',
        options: { state: { referrer: `${location.pathname}?${searchParams}` }, replace: true } as const,
      };
    }

    if (tokenExpired) {
      return {
        path: '/oops',
        options: { state: { referrer: location.pathname }, replace: true } as const,
      };
    }

    if (!hasAccess) {
      return {
        path: urlPath,
        options: undefined,
      } as const;
    }

    return null;
  }, [hasAccess, location.pathname, searchParams, token, tokenExpired, urlPath]);

  useEffect(() => {
    if (redirectTarget) {
      navigate(redirectTarget.path, redirectTarget.options);
    }
  }, [navigate, redirectTarget]);

  if (redirectTarget) {
    return null;
  }

  return children;
};

export default RequireAuth;
